import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { ReadinessResult } from "@jilian/core";
import { evaluateReadiness, generateWorkoutPlan, nextTemplate, createDefaultTemplates } from "@jilian/core";
import { EXERCISE_LIBRARY } from "../data/exercises";
import { useRecentSessions, useSettings, useTrainingTemplates } from "../db/hooks";
import { DEFAULT_INJURY, DEFAULT_PROFILE } from "../db/repo";
import { Button, Card, RiskTag, SectionTitle } from "../components/ui";

/** 正常准备度（序列展示用预生成；真实生成在「今日」按恢复问询调整）。 */
function previewReadiness(): ReadinessResult {
  return evaluateReadiness({
    sleepQuality: "good",
    fatigueLevel: "low",
    muscleSoreness: "none",
    footStatus: "stable",
    jointPain: "none",
  });
}

export function Workout() {
  const nav = useNavigate();
  const settings = useSettings();
  const profile = settings?.profile ?? DEFAULT_PROFILE;
  const injury = settings?.injury ?? DEFAULT_INJURY;

  const dbTemplates = useTrainingTemplates(profile.id);
  const recentSessions = useRecentSessions(profile.id);

  const templates = useMemo(() => {
    return dbTemplates && dbTemplates.length > 0 ? dbTemplates : createDefaultTemplates(profile.id);
  }, [dbTemplates, profile.id]);

  const nextTpl = useMemo(() => {
    return nextTemplate(templates, recentSessions ?? []);
  }, [templates, recentSessions]);

  // 各序列预览计划由规则引擎生成
  const plans = useMemo(() => {
    const out = new Map<string, ReturnType<typeof generateWorkoutPlan>>();
    const readiness = previewReadiness();
    for (const seq of templates) {
      out.set(
        seq.id,
        generateWorkoutPlan({
          sequenceCode: seq.code,
          slots: seq.slots,
          isOptionalLightDay: seq.isOptionalLightDay,
          readiness,
          injury,
          exerciseLibrary: EXERCISE_LIBRARY,
        }),
      );
    }
    return out;
  }, [templates, injury]);

  const todayPlan = plans.get(nextTpl.id);

  return (
    <div className="space-y-4 pb-6">
      <header className="py-2">
        <h1 className="text-[28px] font-extrabold text-tone-ink tracking-tight">训练</h1>
        <div className="text-xs font-semibold text-tone-soft mt-0.5">训练序列 · 计划编辑 · 动作库</div>
      </header>

      {/* 今日训练入口 */}
      <Card className="p-4 border border-tone-border shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold text-tone-accent tracking-wider uppercase">
              轮转推荐
            </div>
            <div className="text-[16px] font-bold text-tone-ink mt-0.5">
              今日训练 · 序列 {nextTpl.code}（{nextTpl.name}）
            </div>
            <div className="text-[12px] text-tone-soft mt-0.5">
              预计 {todayPlan?.estimatedMinutes ?? "—"} 分钟（{todayPlan?.exercises.length ?? "—"} 个动作）
            </div>
          </div>
          <Button onClick={() => nav("/")}>去训练</Button>
        </div>
      </Card>

      {/* 训练序列 */}
      <div className="flex items-center justify-between pt-1">
        <SectionTitle hint="按 position 确定性轮转">训练序列</SectionTitle>
        <button
          onClick={() => nav("/plan-editor")}
          className="text-xs font-bold text-tone-accent active:opacity-70 px-2 py-1"
        >
          自定义序列 →
        </button>
      </div>

      <div className="space-y-2.5 mb-2">
        {templates.map((seq) => {
          const p = plans.get(seq.id);
          const isNext = seq.id === nextTpl.id;
          return (
            <Card
              key={seq.id}
              className={`p-3.5 flex items-center justify-between transition-all ${
                isNext ? "border-tone-accent/60 bg-tone-accent/[0.03]" : ""
              }`}
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-bold text-tone-ink">序列 {seq.code}</span>
                  <span className="text-[13px] font-semibold text-tone-ink">{seq.name}</span>
                  {seq.isOptionalLightDay ? (
                    <RiskTag tone="accent">可选</RiskTag>
                  ) : (
                    <RiskTag tone="good">正式</RiskTag>
                  )}
                  {isNext ? <RiskTag tone="warn">今日待练</RiskTag> : null}
                </div>
                <div className="text-[12px] text-tone-soft">
                  {p ? `${p.exercises.length} 个动作 · 约 ${p.estimatedMinutes} 分钟` : "—"}
                </div>
              </div>

              <Button
                variant="secondary"
                className="text-xs px-3 min-h-[32px]"
                onClick={() => nav(`/plan-editor?id=${seq.id}`)}
              >
                编辑
              </Button>
            </Card>
          );
        })}
      </div>

      {/* 动作库独立入口卡片 */}
      <Card
        className="p-4 cursor-pointer hover:border-tone-accent/50 transition-all border border-tone-border shadow-sm mt-4"
        onClick={() => nav("/library")}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-tone-accent/10 flex items-center justify-center text-tone-accent">
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div>
              <div className="text-[15px] font-bold text-tone-ink">动作库检索</div>
              <div className="text-[12px] text-tone-soft mt-0.5">
                浏览全部 {EXERCISE_LIBRARY.length} 个动作 · 一级部位与二级器械筛选
              </div>
            </div>
          </div>
          <span className="text-tone-soft text-lg">›</span>
        </div>
      </Card>
    </div>
  );
}
